<script lang="ts">
  import { onMount } from 'svelte';
  import { page } from '$app/state';
  import { env } from '$env/dynamic/public';
  import { isPreview } from '$lib/stores/preview';
  import {
    type NewsCard,
    type NewsCardContent,
    type NewsCardContentArray
  } from '$lib/sanity/schemas';
  import { accessibilityPreferences } from '$lib/stores/accessibility';
  import { EntityType, MiscTypes } from '$lib/sanity/types';
  import { ResponsiveVars, TwistDivider, TwistGroup } from '@twist/core';
  import { cx } from '@emotion/css';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { documentPagePath, newsPostPath } from '$lib/utils/routes';
  import { isHrefExternal } from '$lib/utils/links';
  import { ReadMoreBadge } from '../Badge';
  import Image from './Image/Image.svelte';
  import {
    useEncodeDataAttribute,
    useQuery,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import HeadingSizeDecorator from '$components/Common/HeadingSizeDecorator.svelte';
  import { sanityStudioURL } from '$lib/client';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import { getIconByShape } from '$components/News/ButtonShapes';
  import { generatePagePathFromSlug } from '$lib/utils/pages/routes';
  import dayjs from 'dayjs';
  import { automatedCardsStore } from '$lib/news/automatedCards/store';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';
  import { GetAutomatedCardsQueryWithPages } from '$lib/sanity/queries/news/newsCard';

  interface Props {
    document: NewsCard;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  let cardId = $derived(document.itemId || '');
  let card = $derived($automatedCardsStore.cards[cardId]);
  let query = $derived(
    card
      ? useQuery<NewsCardContentArray>(
          GetAutomatedCardsQueryWithPages,
          {
            ...$automatedCardsStore.sections[card.sectionId]?.params,
            ids: $automatedCardsStore.sections[card.sectionId]?.params?.skipIds
          },
          { initial: $automatedCardsStore.sections[card.sectionId]?.data }
        )
      : null
  );

  let automatedData = $derived($query?.data ? $query?.data[card.index] : undefined);

  let variantProperties = $derived(
    document.variant.type === 'vertical'
      ? [`${document.variant.topElement}TopElement`]
      : [`${document.variant.imagePosition}ImagePosition`, `${document.variant.alignment}Alignment`]
  );
  let content = $derived(document.content || automatedData);

  let cardSourceMap = $derived(document.content ? sourceMap : $query?.sourceMap);
  let cardPath = $derived(document.content ? [...path, 'content'] : [card?.index]);
  let encodeDataAttribute = $derived(
    useEncodeDataAttribute(content, cardSourceMap, sanityStudioURL)
  );

  // If the card content is a news post reference,
  // extract the fields into the shape of CardContentFields
  let cardContent = $derived(
    content?._type === EntityType.NewsPost || content?._type === EntityType.NewsBasicPost
      ? {
          headline: content.name,
          image: content.cardContent?.content?.image || content.poster,
          tags: content.tags,
          publicationDate: content.publicationInfo?.publicationDate || '',
          summary: content.summary ?? null
        }
      : content?.cardContent?.content
  );

  let isProdEnv = $state(false);

  onMount(() => {
    const environmentValue = page.data.testEnv
      ? window?.MOCK_TEST_ENV || 'development'
      : env.PUBLIC_ENVIRONMENT || 'development';

    isProdEnv = environmentValue === 'production';
  });

  const responsiveVars = new ResponsiveVars()
    .addFluid('spacer', [
      ['min', 24],
      ['sm', 'lg', 24, 48],
      ['lg', 'max', 18, 36],
      ['max', 36]
    ])
    .addFluid('horizontal-variant-gap', toFluidVarInput(30, 68))
    .toCSS();

  const createCardLink = (content: NewsCardContent) => {
    if (content._type === EntityType.NewsPost || content._type === EntityType.NewsBasicPost) {
      return newsPostPath(content.slug.current);
    } else if (content._type === MiscTypes.ExternalContent) {
      return content.url;
    } else if (content._type === EntityType.Page) {
      return generatePagePathFromSlug(content.slug.current, content.hierarchyItems);
    } else if (content._type === MiscTypes.Document) {
      return documentPagePath(content.slug.current);
    }
    return '';
  };
</script>

{#if !card?.error && content}
  <TwistGroup>
    {#snippet children({ active: groupActive }: { active: boolean })}
      <a
        href={createCardLink(content)}
        target={isHrefExternal(createCardLink(content)) ? '_blank' : undefined}
        class={cx(responsiveVars, 'card-root')}
        class:active={groupActive}
      >
        <div
          class={`card ${document.variant.type} ${variantProperties.join(' ')}`}
          class:no-image={cardContent?.image}
        >
          {#if cardContent?.image}
            <div class="image">
              <ReadMoreBadge
                placement={document.badge}
                mask={document.imageMask || 'square'}
                animate={groupActive}
                sourceMap={cardSourceMap}
                path={content?._type === EntityType.NewsPost ||
                content?._type === EntityType.NewsBasicPost
                  ? [...cardPath, 'badge']
                  : [...cardPath, 'cardContent', 'content', 'badge']}
              />
              <Image
                animate={groupActive}
                image={{
                  image: cardContent.image,
                  aspectRatio: document.aspectRatio,
                  imageMask: document.imageMask ?? 'square'
                }}
                sourceMap={cardSourceMap}
                path={content?._type === EntityType.NewsPost ||
                content?._type === EntityType.NewsBasicPost
                  ? [...cardPath, 'poster', '_type']
                  : [...cardPath, 'cardContent', 'content', 'image', '_type']}
              />
            </div>
          {/if}

          <div class="content">
            <div class="divider">
              <TwistDivider thickness="1px" margin={0} />
            </div>

            {#if cardContent?.publicationDate}
              <div
                class="publicationDate"
                data-sanity={encodeDataAttribute([
                  ...(content?._type === EntityType.NewsPost ||
                  content?._type === EntityType.NewsBasicPost
                    ? [...cardPath, 'publicationInfo', 'publicationDate']
                    : [...cardPath, 'cardContent', 'content', 'publicationDate'])
                ])}
              >
                <FontSizeDecorator size="breadcrumb">
                  {dayjs(cardContent.publicationDate).format('MMMM DD, YYYY')}
                </FontSizeDecorator>
              </div>
            {/if}

            <h3
              class="headline"
              data-sanity={encodeDataAttribute([
                ...(content?._type === EntityType.NewsPost ||
                content?._type === EntityType.NewsBasicPost
                  ? [...cardPath, 'name']
                  : [...cardPath, 'cardContent', 'content', 'headline'])
              ])}
            >
              <HeadingSizeDecorator size={document.headlineSize}>
                {cardContent?.headline}
              </HeadingSizeDecorator>
            </h3>

            <div
              class="summary"
              data-sanity={encodeDataAttribute([
                ...(content?._type === EntityType.NewsPost ||
                content?._type === EntityType.NewsBasicPost
                  ? [...cardPath, 'summary']
                  : [...cardPath, 'cardContent', 'content', 'summary'])
              ])}
            >
              <FontSizeDecorator size="normal">
                {cardContent?.summary ?? ''}
              </FontSizeDecorator>
            </div>

            <div class="button">
              {#if cardContent?.image}
                <BlockButton
                  tabindex={-1}
                  aria-hidden="true"
                  motion={$accessibilityPreferences.reduceMotion ? 'never' : 'when-active'}
                  {groupActive}
                  data-sanity={encodeDataAttribute([...path, 'buttonText'])}
                >
                  {document.buttonText || 'READ MORE'}
                  {#snippet icon()}
                    {@const SvelteComponent = getIconByShape('oval')}
                    <SvelteComponent />
                  {/snippet}
                </BlockButton>
              {:else}
                <BlockButton
                  tabindex={-1}
                  aria-hidden="true"
                  motion={$accessibilityPreferences.reduceMotion ? 'never' : 'when-active'}
                  {groupActive}
                >
                  {#snippet icon()}
                    {@const SvelteComponent_1 = getIconByShape('oval')}
                    <SvelteComponent_1 />
                  {/snippet}
                </BlockButton>
              {/if}
            </div>
          </div>
        </div>
      </a>
    {/snippet}
  </TwistGroup>
{:else if card?.error && (!isProdEnv || $isPreview)}
  <div class="card-error">
    <h2>Error parsing card content</h2>
    <p><b>Card Type:</b> {card.error._type}</p>
    <p><b>Id:</b> {card.error._id}</p>
    <p><b>Name:</b> {card.error.name || 'N/A'}</p>
    <h3>Error</h3>
    <p>{card.error.message}</p>
  </div>
{/if}

<style lang="scss">
  @import 'src/scss/accessibility';
  @import 'src/scss/media';
  @import 'src/scss/variables';

  .card-root {
    cursor: pointer;

    &,
    &:focus,
    &:hover,
    &:visited {
      color: $text-default-color;
      text-decoration: none;
    }
  }

  @include without-reduce-motion {
    .card-root:focus {
      outline: none;
    }
  }

  .headline {
    font-size: inherit;
  }

  .card {
    display: grid;

    .publicationDate,
    .headline,
    .summary,
    .image {
      margin-bottom: var(--spacer);
    }

    .divider {
      margin-bottom: var(--spacer);
    }

    &.vertical {
      &.headlineTopElement {
        grid-template-areas:
          'divider'
          'publicationDate'
          'headline'
          'image'
          'summary'
          'button';

        & > .content {
          display: contents;
        }
      }

      &.imageTopElement {
        grid-template-areas: 'image' 'content';

        .divider {
          margin-top: var(--spacer);
        }
      }

      &.no-image {
        .divider {
          margin-top: 0;
        }
      }
    }

    &.horizontal {
      // Render vertial alignment variant on mobile

      &.leftImagePosition {
        grid-template-areas: 'image' 'content';

        .divider {
          margin-top: var(--spacer);

          @include at('lg') {
            margin-top: 0;
          }
        }
      }

      &.rightImagePosition {
        grid-template-areas:
          'divider'
          'publicationDate'
          'headline'
          'image'
          'summary'
          'button';

        & > .content {
          display: contents;
        }
      }

      // Render horizontal variant on desktop
      @include at('lg') {
        grid-template-columns: repeat(2, 1fr);
        grid-column-gap: var(--horizontal-variant-gap);

        &.leftImagePosition {
          grid-template-areas: 'image content';
        }

        &.rightImagePosition {
          grid-template-areas: 'content image';

          & > .content {
            display: block;
          }
        }

        .divider {
          margin-top: 0;
        }
      }

      &.middleAlignment {
        .content,
        .image {
          margin: auto 0;
        }
      }

      &.bottomAlignment {
        .content,
        .image {
          margin: auto 0 0;
        }
      }
    }

    .image {
      grid-area: image;
      width: 100%;
      position: relative;
    }

    .content {
      grid-area: content;
      overflow: hidden;
      overflow-wrap: break-word;
    }

    .divider {
      grid-area: divider;
    }

    .publicationDate {
      grid-area: publicationDate;
    }

    .headline {
      grid-area: headline;
    }

    .summary {
      grid-area: summary;
    }

    .button {
      grid-area: button;
    }
  }

  .card-error {
    padding: 24px;
    color: white;
    background: black;
    font-family: var(--font-family-primary);

    p {
      white-space: pre-line;
    }

    h2 {
      margin-bottom: 8px;
    }

    h3 {
      margin-top: 8px;
    }
  }
</style>
